// generateGraph.ts
import type { NodeDataType } from './GraphType';
import dagre from '@dagrejs/dagre';
import { type Node, type Edge, MarkerType } from '@xyflow/react';
import { WorkflowActionDTOModel } from '@repositories/workflowRepository/models/WorkflowActionDTOModel';
import { WorkflowTriggerDTOModel } from '@repositories/workflowRepository/models/WorkflowTriggerDTOModel';

// Custom Node type with NodeDataType for v12 compatibility
export type WorkflowNode = Node<NodeDataType, string>;

const generateTriggerNode = (model?: WorkflowTriggerDTOModel): WorkflowNode => {
  /**
   * 트리거 노드 생성
   * @param model 트리거 모델
   * @returns 트리거 노드
  1   * 트리거 노드는 서버에 position을 저장하지 않으므로 드래그 비활성화
   */
  return {
    id: `trigger`,
    type: 'trigger',
    position: { x: 0, y: 0 },
    draggable: false, // 트리거 노드는 드래그 불가 (서버에 position 저장 안됨)
    data: {
      index: 0,
      type: 'trigger',
      triggerModel: model ? model.toCreateDTO() : new WorkflowTriggerDTOModel({}).toCreateDTO(),
      baseTriggerModel: model ? model.baseTrigger : undefined,
    },
  };
};

export const generateActionNode = (actionIndex: number, model?: WorkflowActionDTOModel): WorkflowNode => {
  /**
   * 액션 노드 생성
   * @param actionIndex 액션 인덱스
   * @param model 액션 모델
   * @returns 액션 노드
   */
  // 서버에서 받아온 position이 있으면 사용
  const hasServerPosition = model?.positionX != null && model?.positionY != null;

  return {
    id: `action-${actionIndex}`,
    type: 'action',
    data: {
      index: actionIndex + 1,
      type: 'action',
      actionModel: model
        ? model.toCreateDTO()
        : new WorkflowActionDTOModel({ type: 'CONNECTION_ACTION' }).toCreateDTO(),
      connectionModel: model ? model.connection : undefined,
      isSmpAction: model ? model.type === 'CONNECTION_ACTION' && !model.connection?.connectionId : false,
      actionIndex,
      hasServerPosition,
    },
    position: {
      x: hasServerPosition ? model.positionX! : 0,
      y: hasServerPosition ? model.positionY! : 0,
    },
  };
};

export const generateConditionNode = (conditionIndex: number, model?: WorkflowActionDTOModel): WorkflowNode => {
  /**
   * 조건 노드 생성
   * @param conditionIndex 조건 인덱스
   * @param model 액션 모델 (IF_ACTION 타입)
   * @returns 조건 노드
   */
  // 서버에서 받아온 position이 있으면 사용
  const hasServerPosition = model?.positionX != null && model?.positionY != null;

  return {
    id: `condition-${conditionIndex}`,
    type: 'condition',
    data: {
      index: conditionIndex + 1,
      type: 'condition',
      actionModel: model
        ? model.toCreateDTO()
        : new WorkflowActionDTOModel({
            type: 'IF_ACTION',
            actionName: 'Condition Branch(IF)',
          }).toCreateDTO(),
      connectionModel: undefined,
      isSmpAction: false,
      conditionIndex,
      hasServerPosition,
    },
    position: {
      x: hasServerPosition ? model.positionX! : 0,
      y: hasServerPosition ? model.positionY! : 0,
    },
  };
};

export const generateEndNode = (actionIndex: number, model?: WorkflowActionDTOModel): WorkflowNode => {
  /**
   * 종료 노드 생성
   * @param actionIndex 액션 인덱스
   * @param model 액션 모델 (END_ACTION 타입, 서버에서 불러올 때 사용)
   * @returns 종료 노드
   */
  // 서버에서 받아온 position이 있으면 사용
  const hasServerPosition = model?.positionX != null && model?.positionY != null;

  return {
    id: `end-${actionIndex}`,
    type: 'end',
    position: {
      x: hasServerPosition ? model.positionX! : 0,
      y: hasServerPosition ? model.positionY! : 0,
    },
    data: {
      index: -1,
      type: 'end',
      actionModel: model
        ? model.toCreateDTO()
        : new WorkflowActionDTOModel({ type: 'END_ACTION', actionName: 'End' }).toCreateDTO(),
      hasServerPosition,
    },
  };
};

export const generateNormalEdge = (sourceId: string, targetId: string) => {
  /**
   * 일반 엣지 생성
   * @param sourceId 소스 노드 ID
   * @param targetId 타겟 노드 ID
   * @returns 일반 엣지
   */
  return {
    id: `${sourceId}:${targetId}`,
    source: sourceId,
    target: targetId,
    sourceHandle: 'normal',
    targetHandle: 'normal',
    type: 'normal',
    data: {
      edgeType: 'default',
    },
    markerEnd: {
      type: MarkerType.ArrowClosed,
    },
  };
};

export const generateExceptionEdge = (sourceId: string, targetId: string) => {
  /**
   * 예외 엣지 생성
   * @param sourceId 소스 노드 ID
   * @param targetId 타겟 노드 ID
   * @returns 예외 엣지
   */
  return {
    id: `${sourceId}:${targetId}`,
    source: sourceId,
    target: targetId,
    sourceHandle: 'exception',
    targetHandle: 'normal', // target 노드의 Handle id는 'normal'
    type: 'exception',
    data: { edgeType: 'default' },
    markerEnd: {
      type: MarkerType.ArrowClosed,
    },
  };
};

export const generateConditionThenEdge = (sourceId: string, targetId: string) => {
  /**
   * 조건 엣지 생성
   * @param sourceId 소스 노드 ID
   * @param targetId 타겟 노드 ID
   * @returns 조건 엣지
   */
  return {
    id: `${sourceId}:${targetId}`,
    source: sourceId,
    target: targetId,
    sourceHandle: 'conditionThen',
    targetHandle: 'normal', // target 노드의 Handle id는 'normal'
    type: 'conditionThen',
    data: { edgeType: 'default' },
    markerEnd: {
      type: MarkerType.ArrowClosed,
    },
  };
};

const nodeSizes = new Map([
  ['trigger', { width: 376, height: 110 }],
  ['action', { width: 376, height: 110 }],
  ['condition', { width: 376, height: 110 }],
  ['end', { width: 100, height: 50 }],
]);

/**
 * 중복 엣지 제거
 */
const deduplicateEdges = (edges: Edge[]): Edge[] => {
  const edgeMap = new Map<string, Edge>();
  edges.forEach(edge => {
    const key = `${edge.source}:${edge.target}:${edge.type}`;
    if (!edgeMap.has(key)) {
      edgeMap.set(key, edge);
    }
  });
  return Array.from(edgeMap.values());
};

// 레이아웃 상수
const NODE_WIDTH = 376;
const NODE_HEIGHT = 110;
const VERTICAL_GAP = 50; // 노드 간 수직 간격
const HORIZONTAL_OFFSET = 200; // 분기 노드 수평 오프셋

/**
 * 노드가 서버에서 받아온 위치를 가지고 있는지 확인
 */
const hasServerPosition = (node: WorkflowNode): boolean => {
  return node.data.hasServerPosition === true;
};

/**
 * 노드가 유효한 위치를 가지고 있는지 확인
 * 서버에서 받아온 position이 있거나, (0,0)이 아니면 유효
 */
const hasValidPosition = (node: WorkflowNode): boolean => {
  if (node.data.hasServerPosition) return true;
  if (node.position.x !== 0 || node.position.y !== 0) return true;
  return false;
};

/**
 * 새로 추가된 노드의 위치를 연결된 노드 기준으로 계산하고,
 * 필요한 경우 하위 노드들을 아래로 밀어냄
 */
const calculateNewNodePositionAndShift = (
  node: WorkflowNode,
  nodeMap: Map<string, WorkflowNode>,
  edges: Edge[],
  nodesToShift: Map<string, { x: number; y: number }>, // 위치 조정이 필요한 노드들
  calculatedPositions: Map<string, { x: number; y: number }>, // 이미 계산된 새 노드들의 위치
): { x: number; y: number } => {
  const size = nodeSizes.get(node.type ?? 'action') ?? { width: NODE_WIDTH, height: NODE_HEIGHT };

  // sourceNode의 위치를 가져오는 헬퍼 함수
  const getSourcePosition = (sourceNode: WorkflowNode) => {
    // 이미 계산된 위치가 있으면 사용
    const calculated = calculatedPositions.get(sourceNode.id);
    if (calculated) return calculated;
    // 아니면 원래 위치 사용
    return sourceNode.position;
  };

  // sourceNode가 유효한 위치를 가지고 있는지 확인 (계산된 위치 포함)
  const sourceHasValidPosition = (sourceNode: WorkflowNode) => {
    if (calculatedPositions.has(sourceNode.id)) return true;
    return hasValidPosition(sourceNode);
  };

  // 들어오는 엣지 찾기
  const incomingEdge = edges.find(e => e.target === node.id);
  if (incomingEdge) {
    const sourceNode = nodeMap.get(incomingEdge.source);
    if (sourceNode && sourceHasValidPosition(sourceNode)) {
      const sourceSize = nodeSizes.get(sourceNode.type ?? 'action') ?? { width: NODE_WIDTH, height: NODE_HEIGHT };
      const sourcePosition = getSourcePosition(sourceNode);

      // 새 노드의 위치 계산
      let newPosition: { x: number; y: number };

      if (incomingEdge.type === 'conditionThen' || incomingEdge.type === 'exception') {
        // 분기 엣지: 오른쪽 아래로 배치
        newPosition = {
          x: sourcePosition.x + HORIZONTAL_OFFSET,
          y: sourcePosition.y + sourceSize.height + VERTICAL_GAP,
        };
      } else {
        // normal 엣지: 바로 아래로 배치
        newPosition = {
          x: sourcePosition.x + (sourceSize.width - size.width) / 2,
          y: sourcePosition.y + sourceSize.height + VERTICAL_GAP,
        };
      }

      // 새 노드 아래에 있는 기존 노드들을 밀어냄
      const shiftAmount = size.height + VERTICAL_GAP;
      shiftDescendantNodes(node.id, newPosition.y, shiftAmount, nodeMap, edges, nodesToShift);

      return newPosition;
    }
  }

  // 나가는 엣지로 위치 추론 (역방향) - 이 경우는 밀어내기 불필요
  const outgoingEdge = edges.find(e => e.source === node.id);
  if (outgoingEdge) {
    const targetNode = nodeMap.get(outgoingEdge.target);
    if (targetNode && hasValidPosition(targetNode)) {
      const targetSize = nodeSizes.get(targetNode.type ?? 'action') ?? { width: NODE_WIDTH, height: NODE_HEIGHT };
      return {
        x: targetNode.position.x + (targetSize.width - size.width) / 2,
        y: targetNode.position.y - size.height - VERTICAL_GAP,
      };
    }
  }

  return { x: 0, y: 0 };
};

/**
 * 특정 노드의 하위 노드들(자손)을 아래로 밀어냄
 */
const shiftDescendantNodes = (
  newNodeId: string,
  newNodeY: number,
  shiftAmount: number,
  nodeMap: Map<string, WorkflowNode>,
  edges: Edge[],
  nodesToShift: Map<string, { x: number; y: number }>,
) => {
  // 새 노드에서 나가는 엣지의 타겟 노드들 찾기
  const outgoingEdges = edges.filter(e => e.source === newNodeId);

  outgoingEdges.forEach(edge => {
    const targetNode = nodeMap.get(edge.target);
    if (targetNode && hasValidPosition(targetNode)) {
      // 타겟 노드와 그 자손들을 BFS로 밀어냄
      const visited = new Set<string>();
      const queue = [targetNode.id];

      while (queue.length > 0) {
        const currentId = queue.shift()!;
        if (!visited.has(currentId)) {
          visited.add(currentId);

          const currentNode = nodeMap.get(currentId);
          if (currentNode) {
            // 이미 이동 예정인 위치가 있으면 그것을 기준으로, 아니면 현재 위치 기준
            const currentPosition = nodesToShift.get(currentId) ?? currentNode.position;

            // 새 노드보다 아래에 있는 노드만 밀어냄
            if (currentPosition.y >= newNodeY) {
              nodesToShift.set(currentId, {
                x: currentPosition.x,
                y: currentPosition.y + shiftAmount,
              });
            }

            // 자손 노드들 큐에 추가
            const childEdges = edges.filter(e => e.source === currentId);
            childEdges.forEach(childEdge => {
              if (!visited.has(childEdge.target)) {
                queue.push(childEdge.target);
              }
            });
          }
        }
      }
    }
  });
};

export function getLayoutedElements(
  nodes: WorkflowNode[],
  edges: Edge[],
  useServerPosition: boolean = false,
): { nodes: WorkflowNode[]; edges: Edge[] } {
  if (nodes.length === 0) return { nodes: [], edges: [] };

  // 노드 ID Set 생성
  const nodeIds = new Set(nodes.map(n => n.id));

  // 존재하는 노드만 참조하는 엣지 필터링 (부모가 없는 엣지 제거)
  const validEdges = edges.filter(edge => nodeIds.has(edge.source) && nodeIds.has(edge.target));
  const uniqueEdges = deduplicateEdges(validEdges);

  // 노드 맵 생성
  const nodeMap = new Map(nodes.map(n => [n.id, n]));

  // 서버에서 받아온 position이 있는 노드가 있는지 확인 (트리거 제외)
  // 신규 생성 시에는 모든 노드가 hasServerPosition: false이므로 Dagre 사용
  const nonTriggerNodes = nodes.filter(n => n.type !== 'trigger');
  const hasAnyServerPosition = nonTriggerNodes.some(n => hasServerPosition(n));

  // 서버에서 받아온 위치가 하나라도 있으면: 기존 위치 유지, 새 노드만 계산
  if (useServerPosition || hasAnyServerPosition) {
    // 트리거 노드에서 첫 번째 액션으로의 엣지 찾기
    const triggerNode = nodes.find(n => n.type === 'trigger');
    const triggerOutEdge = uniqueEdges.find(e => e.source === triggerNode?.id);
    const firstActionNode = triggerOutEdge ? nodeMap.get(triggerOutEdge.target) : undefined;

    // 첫 번째 액션 노드의 위치가 유효한지 확인
    const firstActionHasValidPosition = firstActionNode && hasValidPosition(firstActionNode);

    // 트리거 위치 계산: 첫 번째 액션 노드 위에 배치
    const triggerPosition = firstActionHasValidPosition
      ? { x: firstActionNode.position.x, y: firstActionNode.position.y - NODE_HEIGHT - VERTICAL_GAP }
      : { x: 0, y: 0 };

    // 새 노드 추가 시 기존 노드들의 위치 조정을 저장할 맵
    const nodesToShift = new Map<string, { x: number; y: number }>();

    // 새 노드들을 찾고 정렬: End 노드가 마지막에 처리되도록 (의존성 순서)
    const newNodes = nodes.filter(node => node.type !== 'trigger' && !hasValidPosition(node));
    const sortedNewNodes = [...newNodes.filter(n => n.type !== 'end'), ...newNodes.filter(n => n.type === 'end')];

    // 계산된 위치를 저장할 별도 맵 (nodeMap 직접 수정하지 않음)
    const calculatedPositions = new Map<string, { x: number; y: number }>();

    // 먼저 새 노드들의 위치를 계산하고, 기존 노드들의 shift 정보를 수집
    const newNodePositions = new Map<string, { x: number; y: number }>();
    sortedNewNodes.forEach(node => {
      const calculatedPosition = calculateNewNodePositionAndShift(
        node,
        nodeMap,
        uniqueEdges,
        nodesToShift,
        calculatedPositions, // 이미 계산된 위치 참조용
      );
      newNodePositions.set(node.id, calculatedPosition);
      calculatedPositions.set(node.id, calculatedPosition);
    });

    const layoutedNodes: WorkflowNode[] = nodes.map(node => {
      const size = nodeSizes.get(node.type ?? 'action') ?? { width: NODE_WIDTH, height: NODE_HEIGHT };

      if (node.type === 'trigger') {
        return {
          ...node,
          position: triggerPosition,
          style: { ...node.style, width: size.width },
        };
      }

      // 새로 추가된 노드: 미리 계산한 위치 사용
      const newPosition = newNodePositions.get(node.id);
      if (newPosition) {
        return {
          ...node,
          position: newPosition,
          style: { ...node.style, width: size.width },
        };
      }

      // 기존 노드: shift가 필요하면 적용, 아니면 그대로 유지
      const shiftedPosition = nodesToShift.get(node.id);
      if (shiftedPosition) {
        return {
          ...node,
          position: shiftedPosition,
          style: { ...node.style, width: size.width },
        };
      }

      // 유효한 위치가 있으면 그대로 유지
      return {
        ...node,
        style: { ...node.style, width: size.width },
      };
    });

    return { nodes: layoutedNodes, edges: uniqueEdges };
  }

  // 모든 노드가 새로 생성된 경우: Dagre 레이아웃 계산 (초기 로드)
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));

  // Dagre 그래프 설정
  dagreGraph.setGraph({
    rankdir: 'TB', // Top to Bottom
    ranksep: 150, // 수직 간격 (증가)
    nodesep: 120, // 수평 간격 (증가 - 노드 겹침 방지)
    ranker: 'network-simplex', // 엣지 크로싱/겹침 최소화
  });

  // 노드 추가
  nodes.forEach(node => {
    const size = nodeSizes.get(node.type ?? 'action') ?? { width: NODE_WIDTH, height: NODE_HEIGHT };
    dagreGraph.setNode(node.id, { ...size });
  });

  // 엣지 추가 - 타입별 가중치 설정
  uniqueEdges.forEach(edge => {
    if (edge.type === 'conditionThen' || edge.type === 'exception') {
      // conditionThen (true): 오른쪽으로 분기, minlen 증가로 더 아래에 배치
      dagreGraph.setEdge(edge.source, edge.target, { minlen: 2, weight: 1 });
    } else {
      // normal (false 포함): 메인 플로우, 높은 가중치로 직선 유지
      dagreGraph.setEdge(edge.source, edge.target, { minlen: 1, weight: 5 });
    }
  });

  // 레이아웃 계산
  dagre.layout(dagreGraph);

  // 노드 위치 적용 (Dagre 계산 결과 사용)
  const layoutedNodes: WorkflowNode[] = nodes.map(node => {
    const nodeWithPosition = dagreGraph.node(node.id);
    const size = nodeSizes.get(node.type ?? 'action') ?? { width: NODE_WIDTH, height: NODE_HEIGHT };

    return {
      ...node,
      position: {
        x: nodeWithPosition.x - size.width / 2,
        y: nodeWithPosition.y - size.height / 2,
      },
      style: { ...node.style, width: size.width },
    };
  });

  return { nodes: layoutedNodes, edges: uniqueEdges };
}

interface GenerateGraphModel {
  trigger?: WorkflowTriggerDTOModel;
  actions: WorkflowActionDTOModel[];
}

export const generateGraph = ({ trigger, actions }: GenerateGraphModel) => {
  const nodes: WorkflowNode[] = [];
  const edges: Edge[] = [];
  const visitedMap = new Map<number, WorkflowNode>();

  // 1. 트리거 노드 추가
  const triggerNode = generateTriggerNode(trigger);
  nodes.push(triggerNode);

  // 2. 트리거에서 첫 번째 액션으로 엣지 추가
  if (actions.length > 0) {
    const actionNode = generateGraphFromAction(0);
    if (actionNode) {
      edges.push(generateNormalEdge(triggerNode.id, actionNode.id));
    }
  } else {
    // 3. 액션이 없으면 바로 End로
    const endNode = generateEndNode(0);
    nodes.push(endNode);
    edges.push(generateNormalEdge(triggerNode.id, endNode.id));
  }

  // 4. 재귀적으로 액션 노드 생성
  function generateGraphFromAction(actionIndex: number): WorkflowNode | undefined {
    if (!actions[actionIndex]) return undefined;

    if (visitedMap.has(actionIndex)) {
      return visitedMap.get(actionIndex);
    }

    const action = actions[actionIndex];
    if (!action) return undefined;

    // 액션 타입에 따라 노드 생성
    // END_ACTION 타입인 경우 End 노드로 생성 (서버에서 불러올 때)
    if (action.type === 'END_ACTION') {
      const endIndex = actions.slice(0, actionIndex).filter(a => a.type === 'END_ACTION').length;
      const endNode = generateEndNode(endIndex, action);
      nodes.push(endNode);
      visitedMap.set(actionIndex, endNode);
      // End 노드는 더 이상 연결이 없으므로 바로 반환
      return endNode;
    }

    let actionNode: WorkflowNode;
    if (action.type === 'IF_ACTION') {
      // 조건 노드 인덱스 카운트 (IF_ACTION만 별도로 카운트)
      const conditionIndex = actions.slice(0, actionIndex).filter(a => a.type === 'IF_ACTION').length;
      actionNode = generateConditionNode(conditionIndex, action);
    } else {
      // 일반 액션 노드 인덱스 카운트 (IF_ACTION, END_ACTION 제외)
      const normalActionIndex = actions
        .slice(0, actionIndex)
        .filter(a => a.type !== 'IF_ACTION' && a.type !== 'END_ACTION').length;
      actionNode = generateActionNode(normalActionIndex, action);
    }

    nodes.push(actionNode);

    visitedMap.set(actionIndex, actionNode);

    // nextStep 엣지 처리
    // NOTE. -1 이면 종료, undefined 이면 +1
    let hasNext = false;
    const nextActionStep = action.nextStep ?? (actions[actionIndex + 1] ? actionIndex + 1 : -1);
    if (nextActionStep >= 0) {
      hasNext = true;
      const targetActionNode = generateGraphFromAction(nextActionStep);
      if (targetActionNode) {
        edges.push(generateNormalEdge(actionNode.id, targetActionNode.id));
      }
    }

    // 리프 노드인 경우 종료 노드 추가 (서버에 END_ACTION이 없는 기존 워크플로우)
    if (!hasNext) {
      const endIndex = actions.filter(a => a.type === 'END_ACTION').length + actionIndex;
      const endNode = generateEndNode(endIndex);
      nodes.push(endNode);
      edges.push(generateNormalEdge(actionNode.id, endNode.id));
    }

    // conditionThenStep 엣지 처리
    const conditionThenStep = action.conditionThenStep ?? -1;
    if (conditionThenStep >= 0) {
      const targetConditionThenNode = generateGraphFromAction(conditionThenStep);
      if (targetConditionThenNode) {
        edges.push(generateConditionThenEdge(actionNode.id, targetConditionThenNode.id));
      }
    }

    // exceptionCatchStep 엣지 처리
    const exceptionCatchStep = action.exceptionCatchStep ?? -1;
    if (exceptionCatchStep >= 0) {
      const targetExceptionNode = generateGraphFromAction(exceptionCatchStep);
      if (targetExceptionNode) {
        edges.push(generateExceptionEdge(actionNode.id, targetExceptionNode.id));
      }
    }

    return actionNode;
  }

  // 레이아웃 적용
  return getLayoutedElements(nodes, edges);
};

export type NodeTraverseInfo = {
  prevNode?: WorkflowNode;
  incomingEdge?: Edge;
  incomingEdges: Edge[];
  outgoingEdges: Edge[];
};

export const traverseGraph = (
  nodes: WorkflowNode[],
  edges: Edge[],
  callback: (node: WorkflowNode, nodeInfo: NodeTraverseInfo) => void,
) => {
  const triggerNode = nodes[0];

  if (!triggerNode) return;

  // 맵 구성
  const nodeMap = new Map<string, WorkflowNode>();
  nodes.forEach(node => {
    nodeMap.set(node.id, node);
  });
  const outgoingEdgeMap = new Map<string, Edge[]>();
  edges.forEach(edge => {
    const items = outgoingEdgeMap.get(edge.source);
    if (!items) {
      outgoingEdgeMap.set(edge.source, [edge]);
    } else {
      items.push(edge);
    }
  });
  const incomingEdgeMap = new Map<string, Edge[]>();
  edges.forEach(edge => {
    const items = incomingEdgeMap.get(edge.target);
    if (!items) {
      incomingEdgeMap.set(edge.target, [edge]);
    } else {
      items.push(edge);
    }
  });

  const triggerEdge = outgoingEdgeMap.get(triggerNode.id)?.[0];
  if (!triggerEdge) return;

  const firstActionNode = nodeMap.get(triggerEdge.target);
  if (!firstActionNode) return;

  // 트리거 노드 방문
  callback(triggerNode, { incomingEdges: [], outgoingEdges: [triggerEdge] });

  const visited = new Set<string>();

  // 재귀적으로 순회
  function traverseActionNode(
    actionNode: WorkflowNode,
    prevNode: WorkflowNode,
    edge: Edge,
    callback: (node: WorkflowNode, nodeInfo: NodeTraverseInfo) => void,
  ) {
    if (visited.has(actionNode.id)) {
      return;
    }
    visited.add(actionNode.id);

    const incomingEdges = incomingEdgeMap.get(actionNode.id)?.filter(edge => nodeMap.get(edge.source)) ?? [];
    const outgoingEdges = outgoingEdgeMap.get(actionNode.id)?.filter(edge => nodeMap.get(edge.target)) ?? [];

    // 액션 노드 콜백 호출
    callback(actionNode, { prevNode, incomingEdge: edge, incomingEdges, outgoingEdges });

    // 순서 : normal → conditionThen → exception
    // 일반 노드 방문
    const normalEdges = outgoingEdges.filter(edge => edge.type === 'normal');
    normalEdges.forEach(edge => {
      const nextActionNode = nodeMap.get(edge.target);
      if (nextActionNode) {
        traverseActionNode(nextActionNode, actionNode, edge, callback);
      }
    });

    // 조건 노드 방문
    const conditonThenEdges = outgoingEdges.filter(edge => edge.type === 'conditionThen');
    conditonThenEdges.forEach(edge => {
      const nextActionNode = nodeMap.get(edge.target);
      if (nextActionNode) {
        traverseActionNode(nextActionNode, actionNode, edge, callback);
      }
    });

    // 예외 노드 방문
    const exceptionEdges = outgoingEdges.filter(edge => edge.type === 'exception');
    exceptionEdges.forEach(edge => {
      const nextActionNode = nodeMap.get(edge.target);
      if (nextActionNode) {
        traverseActionNode(nextActionNode, actionNode, edge, callback);
      }
    });
  }

  traverseActionNode(firstActionNode, triggerNode, triggerEdge, callback);
};
